import { Data, Option, Schema } from 'effect';

import { PickupPointSchema } from './PickupPoint';
import type { PickupPoint } from './PickupPoint';
import { decodePlacementId, PlacementIdSchema } from './PlacementId';
import type { PlacementId } from './PlacementId';
export { decodePlacementId } from './PlacementId';
export { PlacementIdSchema } from './PlacementId';
export type { PlacementId } from './PlacementId';

/**
 * Where the parcel goes, as the order carries it.
 *
 * The backend takes one nullable string, but that string stands for three
 * different situations. A courier has no placement. A store pickup has one the
 * backend fixed, and the catalogue ships it as `predefinedPlacementId`. A
 * carrier pickup has one the shopper chose on a map, along with the address the
 * checkout shows them.
 *
 * One nullable string cannot tell those apart, so the code that reads it has to
 * guess, and the code that writes it has to remember which case it is in. This
 * union states the case. `placementIdOf` is the only place that reduces it back
 * to the wire.
 *
 * The schema is the one declaration: the type derives from it, and the
 * constructors below are built over that type.
 */
export const PlacementSchema = Schema.TaggedUnion({
  /** A courier delivers to an address. The shipment carries no placement. */
  None: {},
  /** The backend already placed this shipment. `id` is its own value. */
  Predefined: { id: PlacementIdSchema },
  /** The shopper chose a point on a carrier's map. */
  Pickup: { point: PickupPointSchema },
});

export type Placement = typeof PlacementSchema.Type;

export const Placement = Data.taggedEnum<Placement>();

/** The placement with nothing chosen. */
export const noPlacement: Placement = Placement.None();

/**
 * What `PATCH /orders/{token}/shipments` receives as `placementId`.
 *
 * This is the whole contract with the backend, and it exists once. Every
 * carrier already reduces its own payload to a `PickupPoint`; this reduces a
 * `Placement` to the field that leaves the browser.
 */
export const placementIdOf = (placement: Placement): PlacementId | null =>
  Placement.$match(placement, {
    None: () => null,
    Predefined: (state) => state.id,
    Pickup: (state) => state.point.placementId,
  });

/** The point the checkout draws a pickup box from, when the placement has one. */
export const placementPoint = (placement: Placement): Option.Option<PickupPoint> =>
  Placement.$match(placement, {
    None: () => Option.none(),
    Predefined: () => Option.none(),
    Pickup: (state) => Option.some(state.point),
  });

/**
 * The placement a bare `placementId` reduces to.
 *
 * An order shipment written before the checkout stored a whole placement holds
 * the id and nothing else. It cannot say whether that id came from a store or
 * from a carrier's map, so it reads as `Predefined`: the right value on the
 * wire, and no pickup box. Choosing a point again restores the detail.
 */
export const placementFromId = (placementId: string | null): Placement =>
  placementId === null
    ? Placement.None()
    : Placement.Predefined({ id: decodePlacementId(placementId) });
